import React, { useCallback, useState, useRef, useEffect, useMemo } from 'react';
import {
  ReactFlow,
  ReactFlowProvider,
  addEdge,
  useNodesState,
  useEdgesState,
  Controls,
  MiniMap,
  Background,
  BackgroundVariant,
  Connection,
  Node,
  Edge,
  NodeTypes,
  useReactFlow,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';

import { Save, Loader2, Zap, Brain, GitBranch, Clock, CheckCircle, ListTree, GitMerge, StopCircle, Repeat, AlertTriangle, AlertCircle, X, Play } from 'lucide-react';
import { useToast } from '@centraoneplus/ui';
import {
  WorkflowNodeData,
  TriggerNodeData,
  NodeKind,
  BRANCH_HANDLE_TRUE,
  BRANCH_HANDLE_FALSE,
  SWITCH_HANDLE_DEFAULT,
  getSwitchCaseHandle,
  LOOP_HANDLE_BODY,
  LOOP_HANDLE_AFTER,
} from './types';

// React Flow requires data to be Record<string, unknown>
type FlowNode = Node<WorkflowNodeData>;
import { useOrgTicketTypes } from './hooks/useOrgData';
import { TriggerNode } from './nodes/TriggerNode';
import { ClassifyNode } from './nodes/ClassifyNode';
import { BranchNode } from './nodes/BranchNode';
import { SwitchNode } from './nodes/SwitchNode';
import { MergeNode } from './nodes/MergeNode';
import { ActionNode } from './nodes/ActionNode';
import { DelayNode } from './nodes/DelayNode';
import { EndNode } from './nodes/EndNode';
import { StopNode } from './nodes/StopNode';
import { LoopNode } from './nodes/LoopNode';
import { NodeConfigPanel } from './panels/NodeConfigPanel';
import {
  serializeWorkflow,
  serializeWorkflowV2,
  deserializeRule,
  deserializeRuleUniversal,
  getSchemaVersion,
} from './hooks/useWorkflowSerializer';
import { validateWorkflow, invalidNodeIds, type WorkflowIssue } from './workflowValidation';
import type { AutomationRule } from '../../../services/workflow.service';

// Register custom node types
const nodeTypes: NodeTypes = {
  trigger:  TriggerNode,
  classify: ClassifyNode,
  branch:   BranchNode,
  switch:   SwitchNode,
  merge:    MergeNode,
  action:   ActionNode,
  delay:    DelayNode,
  end:      EndNode,
  stop:     StopNode,
  loop:     LoopNode,
};

// ── Default starter flow ──────────────────────────────────────────────────────
const DEFAULT_NODES: FlowNode[] = [
  {
    id: 'trigger-1',
    type: 'trigger',
    position: { x: 250, y: 50 },
    data: { kind: 'trigger', label: 'Trigger', eventType: 'TICKET_CREATED', channel: 'any' },
  },
  {
    id: 'end-1',
    type: 'end',
    position: { x: 280, y: 240 },
    data: { kind: 'end', label: 'End' },
  },
];

const DEFAULT_EDGES: Edge[] = [
  { id: 'e1', source: 'trigger-1', target: 'end-1', animated: true },
];

// ── Block palette items ───────────────────────────────────────────────────────
const PALETTE_ITEMS: { kind: NodeKind; label: string; icon: React.ReactNode; color: string }[] = [
  // Several triggers on one canvas = one workflow that starts on any of them
  // (e.g. Ticket assigned, Agent replies, Customer replies), each with its own path.
  { kind: 'trigger', label: 'Trigger',       icon: <Play size={14} />,         color: 'bg-green-100 text-green-700 border-green-300' },
  { kind: 'branch', label: 'If / Branch',    icon: <GitBranch size={14} />,    color: 'bg-yellow-100 text-yellow-700 border-yellow-300' },
  { kind: 'switch', label: 'Switch',         icon: <ListTree size={14} />,     color: 'bg-purple-100 text-purple-700 border-purple-300' },
  { kind: 'loop',   label: 'Loop',           icon: <Repeat size={14} />,       color: 'bg-cyan-100 text-cyan-700 border-cyan-300' },
  { kind: 'action', label: 'Action',         icon: <Zap size={14} />,          color: 'bg-orange-100 text-orange-700 border-orange-300' },
  { kind: 'delay',  label: 'Wait / Delay',   icon: <Clock size={14} />,        color: 'bg-indigo-100 text-indigo-700 border-indigo-300' },
  { kind: 'merge',  label: 'Merge',          icon: <GitMerge size={14} />,     color: 'bg-teal-100 text-teal-700 border-teal-300' },
  { kind: 'stop',   label: 'Stop',           icon: <StopCircle size={14} />,   color: 'bg-orange-100 text-orange-700 border-orange-300' },
  { kind: 'end',    label: 'End',            icon: <CheckCircle size={14} />,  color: 'bg-red-100 text-red-700 border-red-300' },
];

// Node kinds that only exist in the v2 (node-graph) schema — a workflow using
// any of these must be saved as a v2 `workflow` definition, not v1 `actions`.
const V2_ONLY_NODE_KINDS = new Set<string>(['switch', 'merge', 'stop', 'loop']);

/**
 * What the builder saves.
 *
 * Simple single-trigger graphs keep the proven v1 path (sequential `actions`).
 * The v2 node-graph definition is saved instead when the graph uses a
 * v2-only block (switch/merge/stop/loop — the v1 serializer would emit an
 * empty `actions` array) or has MORE THAN ONE Trigger: a multi-trigger
 * workflow starts on any of its triggers, each running its own path.
 *
 * v2 payload: `triggerType` = the first trigger (the rule's primary trigger,
 * used for indexing); `actions` omitted so the backend runs the node engine.
 * With one trigger its channel/ticket-type filter stays rule-wide (as before);
 * with several, rule-wide conditions are empty — each Trigger block carries
 * its own filter in the workflow, applied only to its own path.
 */
export function buildSavePayload(nodes: FlowNode[], edges: Edge[], ruleName: string): any[] {
  const kinds = nodes.map((n) => ((n.data as WorkflowNodeData)?.kind as string) ?? '');
  const hasV2Nodes = kinds.some((k) => V2_ONLY_NODE_KINDS.has(k));
  const triggerCount = kinds.filter((k) => k === 'trigger').length;

  const v1: any[] = serializeWorkflow(nodes, edges, ruleName);
  if (!hasV2Nodes && triggerCount <= 1) return v1;

  const v2 = serializeWorkflowV2(nodes, edges, ruleName);
  const trigger = v1[0];
  if (!v2 || !trigger) return v1;
  return [
    {
      name: ruleName,
      triggerType: trigger.triggerType,
      triggerConditions:
        triggerCount > 1 ? { logic: 'AND', conditions: [] } : trigger.triggerConditions,
      schemaVersion: 2,
      workflow: v2.workflow,
      stopOnMatch: false,
      isActive: true,
    },
  ];
}

// ── Default data for each node kind ──────────────────────────────────────────
function defaultNodeData(kind: NodeKind): WorkflowNodeData {
  switch (kind) {
    case 'trigger':  return { kind: 'trigger', label: 'Trigger', eventType: 'TICKET_CREATED', channel: 'any' };
    case 'classify': return { kind: 'classify', label: 'AI Classify' };
    case 'branch':   return { kind: 'branch', label: 'Branch', field: 'entity.ticketTypeId', operator: 'equals', value: '' };
    case 'switch':   return { kind: 'switch', label: 'Switch', field: 'entity.status', cases: [], hasDefault: true };
    case 'merge':    return { kind: 'merge', label: 'Merge' };
    case 'action':   return { kind: 'action', label: 'Action', actionType: '', config: {} };
    case 'delay':    return { kind: 'delay', label: 'Delay', delaySeconds: 300 };
    case 'stop':     return { kind: 'stop', label: 'Stop', reason: '' };
    case 'loop':     return { kind: 'loop', label: 'Loop', itemsPath: 'entity.lineItems', itemVariable: 'item', mode: 'serial', continueOnError: true, outputVariable: 'loopResult' };
    case 'end':      return { kind: 'end', label: 'End' };
  }
}

// ── Inner canvas component (needs ReactFlowProvider context) ──────────────────
interface CanvasProps {
  ruleName: string;
  onSave: (rules: ReturnType<typeof serializeWorkflow>) => Promise<void>;
  initialRule?: AutomationRule;
}

function Canvas({ ruleName, onSave, initialRule }: CanvasProps) {
  const { ticketTypes, loading: typesLoading } = useOrgTicketTypes();
  const toast = useToast();

  const initial = useMemo(() => {
    if (initialRule) {
      // Use universal deserializer that handles both v1 and v2 formats
      return deserializeRuleUniversal(initialRule, { ticketTypes });
    }
    return { nodes: DEFAULT_NODES, edges: DEFAULT_EDGES };
  }, [initialRule, ticketTypes]);

  const [nodes, setNodes, onNodesChange] = useNodesState(initial.nodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initial.edges);
  const [selectedNode, setSelectedNode] = useState<FlowNode | null>(null);
  const [saving, setSaving] = useState(false);
  const nodeIdCounter = useRef(initial.nodes.length + 100);
  const { screenToFlowPosition, setCenter } = useReactFlow();
  // Whether the inline issues panel is dismissed (re-shows on the next edit).
  const [issuesDismissed, setIssuesDismissed] = useState(false);

  // Live structural validation — recomputed on every node/edge change so the
  // builder always shows a message for disconnected blocks and other problems.
  const issues = useMemo(() => validateWorkflow(nodes as any, edges as any), [nodes, edges]);
  const errorIssues = issues.filter((i) => i.severity === 'error');
  const warningIssues = issues.filter((i) => i.severity === 'warning');
  const badNodeIds = useMemo(() => invalidNodeIds(issues), [issues]);

  // Re-show the panel whenever the set of issues changes after a dismissal.
  useEffect(() => {
    setIssuesDismissed(false);
  }, [issues.length]);

  // Outline the offending nodes in red so "which block isn't joined" is obvious.
  const decoratedNodes = useMemo(
    () =>
      nodes.map((n) =>
        badNodeIds.has(n.id)
          ? ({ ...n, className: `${(n as any).className ?? ''} wf-node-invalid`.trim() } as FlowNode)
          : n,
      ),
    [nodes, badNodeIds],
  );

  // Select an issue's node and pan/zoom the canvas to it.
  const focusIssue = useCallback(
    (issue: WorkflowIssue) => {
      if (!issue.nodeId) return;
      const node = nodes.find((n) => n.id === issue.nodeId);
      if (!node) return;
      setSelectedNode(node as FlowNode);
      setCenter(node.position.x + 110, node.position.y + 40, { zoom: 1.2, duration: 400 });
    },
    [nodes, setCenter],
  );

  // Re-sync nodes when ticketTypes finish loading (fixes UUID display on edit)
  useEffect(() => {
    if (initialRule && ticketTypes.length > 0) {
      const resolved = deserializeRuleUniversal(initialRule, { ticketTypes });
      setNodes(resolved.nodes);
      setEdges(resolved.edges);
    }
  }, [ticketTypes]); // eslint-disable-line react-hooks/exhaustive-deps

  const onConnect = useCallback(
    (params: Connection) => {
      // Preserve sourceHandle and add labels for branch/switch connections
      let edgeLabel: string | undefined;
      let labelColor: string | undefined;

      if (params.sourceHandle === BRANCH_HANDLE_TRUE) {
        edgeLabel = 'Yes';
        labelColor = '#16a34a';
      } else if (params.sourceHandle === BRANCH_HANDLE_FALSE) {
        edgeLabel = 'No';
        labelColor = '#dc2626';
      } else if (params.sourceHandle === SWITCH_HANDLE_DEFAULT) {
        edgeLabel = 'Default';
        labelColor = '#6b7280';
      } else if (params.sourceHandle?.startsWith('case_')) {
        edgeLabel = params.sourceHandle.replace('case_', 'Case ');
        labelColor = '#3b82f6';
      } else if (params.sourceHandle === LOOP_HANDLE_BODY) {
        edgeLabel = 'Each item';
        labelColor = '#0891b2';
      } else if (params.sourceHandle === LOOP_HANDLE_AFTER) {
        edgeLabel = 'After';
        labelColor = '#6b7280';
      }

      setEdges((eds) =>
        addEdge(
          {
            ...params,
            animated: true,
            label: edgeLabel,
            labelStyle: edgeLabel ? { fill: labelColor, fontWeight: 600, fontSize: 10 } : undefined,
            labelBgStyle: edgeLabel ? { fill: '#fff', fillOpacity: 0.9 } : undefined,
          },
          eds,
        ),
      );
    },
    [setEdges],
  );

  const onNodeClick = useCallback((_: React.MouseEvent, node: FlowNode) => {
    setSelectedNode(node);
  }, []);

  const onPaneClick = useCallback(() => setSelectedNode(null), []);

  const addNode = (kind: NodeKind) => {
    const id = `${kind}-${++nodeIdCounter.current}`;
    // Position new node at the center of the current viewport
    const center = screenToFlowPosition({
      x: window.innerWidth / 2,
      y: window.innerHeight / 2,
    });
    const newNode: FlowNode = {
      id,
      type: kind,
      position: {
        x: center.x + (Math.random() - 0.5) * 60,
        y: center.y + (Math.random() - 0.5) * 60,
      },
      data: defaultNodeData(kind),
    };
    setNodes((nds) => [...nds, newNode as any]);
    setSelectedNode(newNode);
  };

  const updateNodeData = (nodeId: string, updated: Partial<WorkflowNodeData>) => {
    setNodes((nds) =>
      nds.map((n) =>
        n.id === nodeId ? ({ ...n, data: { ...n.data, ...updated } } as FlowNode) : n,
      ),
    );
    setSelectedNode((sel) =>
      sel?.id === nodeId ? { ...sel, data: { ...sel.data, ...updated } as WorkflowNodeData } : sel,
    );
  };

  const handleSave = async () => {
    // Block save on structural errors (e.g. a block that isn't joined) and tell
    // the user exactly what's wrong; surface the inline panel too.
    const blocking = validateWorkflow(nodes as any, edges as any).filter((i) => i.severity === 'error');
    if (blocking.length > 0) {
      setIssuesDismissed(false);
      toast.error(
        'Cannot save workflow',
        blocking.length === 1
          ? blocking[0].message
          : `${blocking.length} blocks aren't connected. Fix the highlighted issues, then save.`,
      );
      return;
    }

    setSaving(true);
    try {
      // Control-flow nodes (switch/merge/stop/loop) only exist in the v2
      // node-graph schema — the v1 serializer can't represent them and would
      // emit an empty `actions` array (→ backend 400 "At least one action is
      // required"). When the graph uses any of them, save the v2 workflow
      // definition instead: reuse the v1 trigger derivation so the rule still
      // indexes by trigger, and OMIT `actions` so the backend runs the node
      // engine (schemaVersion 2). Simple graphs keep the proven v1 path.
      await onSave(buildSavePayload(nodes, edges, ruleName));
    } catch (err) {
      toast.error('Save failed', err instanceof Error ? err.message : 'Could not save the workflow.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex h-full w-full flex-col overflow-hidden rounded-xl border border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-900">
      {/* Red outline + halo on blocks that aren't joined into the flow. */}
      <style>{`
        .wf-node-invalid { outline: 2px solid #ef4444; outline-offset: 3px; border-radius: 10px; }
        .wf-node-invalid .react-flow__node, .wf-node-invalid > div { box-shadow: 0 0 0 2px rgba(239,68,68,0.25); }
      `}</style>
      {/* Top toolbar */}
      <div className="flex items-center gap-2 border-b border-gray-200 bg-white px-4 py-2 dark:border-gray-700 dark:bg-gray-800">
        <span className="text-xs font-medium text-gray-500 dark:text-gray-400">Add block:</span>
        {PALETTE_ITEMS.map((item) => (
          <button
            key={item.kind}
            onClick={() => addNode(item.kind)}
            className={`flex items-center gap-1.5 rounded-lg border px-2.5 py-1 text-xs font-medium transition-colors hover:opacity-80 ${item.color}`}
          >
            {item.icon}
            {item.label}
          </button>
        ))}
        <div className="ml-auto flex items-center gap-2">
          {errorIssues.length > 0 && (
            <span className="flex items-center gap-1 rounded-md bg-red-100 px-2 py-1 text-xs font-medium text-red-700 dark:bg-red-900/30 dark:text-red-300">
              <AlertCircle size={12} />
              {errorIssues.length} issue{errorIssues.length > 1 ? 's' : ''}
            </span>
          )}
          <button
            onClick={handleSave}
            disabled={saving}
            title={errorIssues.length > 0 ? 'Fix the highlighted issues before saving' : 'Save workflow'}
            className="flex items-center gap-1.5 rounded-lg bg-primary-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-primary-700 disabled:opacity-60"
          >
            {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
            Save workflow
          </button>
        </div>
      </div>

      {/* Inline validation messages — shown for disconnected blocks, missing
          trigger, dangling paths, etc. Click a message to jump to that block. */}
      {!issuesDismissed && issues.length > 0 && (
        <div
          className={`border-b px-4 py-2 text-xs ${
            errorIssues.length
              ? 'border-red-200 bg-red-50 dark:border-red-900/40 dark:bg-red-900/15'
              : 'border-amber-200 bg-amber-50 dark:border-amber-900/40 dark:bg-amber-900/15'
          }`}
        >
          <div className="flex items-start gap-2">
            <span className="mt-0.5 shrink-0">
              {errorIssues.length ? (
                <AlertCircle size={14} className="text-red-600 dark:text-red-400" />
              ) : (
                <AlertTriangle size={14} className="text-amber-600 dark:text-amber-400" />
              )}
            </span>
            <div className="min-w-0 flex-1">
              <p
                className={`font-semibold ${
                  errorIssues.length ? 'text-red-700 dark:text-red-300' : 'text-amber-700 dark:text-amber-300'
                }`}
              >
                {errorIssues.length > 0
                  ? `${errorIssues.length} issue${errorIssues.length > 1 ? 's' : ''} to fix before saving${
                      warningIssues.length ? ` · ${warningIssues.length} warning${warningIssues.length > 1 ? 's' : ''}` : ''
                    }`
                  : `${warningIssues.length} warning${warningIssues.length > 1 ? 's' : ''}`}
              </p>
              <ul className="mt-1 space-y-0.5">
                {issues.map((issue, idx) => (
                  <li key={idx}>
                    <button
                      type="button"
                      onClick={() => focusIssue(issue)}
                      disabled={!issue.nodeId}
                      className={`text-left ${issue.nodeId ? 'cursor-pointer hover:underline' : 'cursor-default'} ${
                        issue.severity === 'error' ? 'text-red-700 dark:text-red-300' : 'text-amber-700 dark:text-amber-300'
                      }`}
                    >
                      <span className="mr-1">{issue.severity === 'error' ? '⛔' : '⚠️'}</span>
                      {issue.message}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
            <button
              type="button"
              onClick={() => setIssuesDismissed(true)}
              className="shrink-0 rounded p-0.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
              aria-label="Dismiss messages"
            >
              <X size={14} />
            </button>
          </div>
        </div>
      )}

      {/* Main area: canvas + side panel */}
      <div className="flex flex-1 overflow-hidden">
        {/* React Flow canvas */}
        <div className="flex-1">
          <ReactFlow
            nodes={decoratedNodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            onNodeClick={onNodeClick}
            onPaneClick={onPaneClick}
            nodeTypes={nodeTypes}
            fitView
            fitViewOptions={{ padding: 0.2 }}
            deleteKeyCode="Delete"
          >
            <Controls />
            <MiniMap nodeStrokeWidth={3} zoomable pannable />
            <Background variant={BackgroundVariant.Dots} gap={16} size={1} color="#d1d5db" />
          </ReactFlow>
        </div>

        {/* Right config panel (slides in when a node is selected) */}
        {selectedNode && (
          <div className="w-72 shrink-0 border-l border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800">
            <NodeConfigPanel
              node={selectedNode}
              onClose={() => setSelectedNode(null)}
              onUpdate={updateNodeData}
            />
          </div>
        )}
      </div>

      {/* Help footer */}
      <div className="border-t border-gray-200 bg-white px-4 py-1.5 dark:border-gray-700 dark:bg-gray-800">
        <p className="text-xs text-gray-400 dark:text-gray-500">
          💡 Click a block to configure it · Drag from a handle to connect blocks · Press Delete to remove selected
        </p>
      </div>
    </div>
  );
}

// ── Public export: wrapped in ReactFlowProvider ───────────────────────────────
interface VisualWorkflowBuilderProps {
  ruleName: string;
  onSave: (rules: ReturnType<typeof serializeWorkflow>) => Promise<void>;
  initialRule?: AutomationRule;
}

export function VisualWorkflowBuilder({ ruleName, onSave, initialRule }: VisualWorkflowBuilderProps) {
  return (
    <ReactFlowProvider>
      <Canvas ruleName={ruleName} onSave={onSave} initialRule={initialRule} />
    </ReactFlowProvider>
  );
}
